import React, { useState } from 'react';
import { X, MessageCircle, Copy, ExternalLink } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SHADOWS } from '../../styles/emziCI';
import { generateWhatsAppLink } from '../../utils/whatsapp';
import toast from '../../utils/toast';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * EMZI NEXUS V6.3 - Bulk Notify Modal
 * Purpose: Send WhatsApp notifications to multiple users
 */

const BulkNotifyModal = ({ isOpen, onClose, selectedUsers }) => {
  const t = useLang();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Load templates on mount
  React.useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
            const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/whatsapp-templates?active_only=true`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        
        // Set default template as selected
        const defaultTemplate = data.templates.find(t => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplateId(defaultTemplate.template_id);
          setMessageTemplate(defaultTemplate.message_body);
        } else if (data.templates.length > 0) {
          // If no default, use first template
          setSelectedTemplateId(data.templates[0].template_id);
          setMessageTemplate(data.templates[0].message_body);
        }
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.template_id === templateId);
    if (template) {
      setMessageTemplate(template.message_body);
    }
  };

  if (!isOpen || !selectedUsers || selectedUsers.length === 0) return null;

  const loginUrl = window.location.origin + '/login';

  const generateMessage = (user) => {
    return messageTemplate
      .replace('{name}', user.full_name || user.email)
      .replace('{email}', user.email)
      .replace('{role}', user.role || 'User')
      .replace('{temp_password}', user.temp_password || '[Not Available]')
      .replace('{login_url}', loginUrl);
  };

  const generateWhatsAppLinks = () => {
    return selectedUsers
      .filter(user => user.phone_e164)
      .map(user => {
        const message = generateMessage(user);
        return generateWhatsAppLink(user.phone_e164, message);
      });
  };

  const handleCopyAllLinks = () => {
    const links = generateWhatsAppLinks();
    const linksText = links.join('\n\n');
    
    const doFallbackCopy = (text) => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success(t('admin.users.bulk.notifyModal.linksCopied'));
      } catch (e) {
        toast.error('Failed to copy links');
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(linksText).then(() => {
        toast.success(t('admin.users.bulk.notifyModal.linksCopied'));
      }).catch(() => {
        doFallbackCopy(linksText);
      });
    } else {
      doFallbackCopy(linksText);
    }
  };

  const handleOpenSequentially = () => {
    const links = generateWhatsAppLinks();
    
    if (links.length === 0) {
      toast.error('No valid phone numbers found');
      return;
    }

    toast.info(`Opening ${links.length} WhatsApp chat(s)...`);
    
    // Open first link immediately
    window.open(links[0], '_blank');
    
    // Open remaining links with delay
    links.slice(1).forEach((link, index) => {
      setTimeout(() => {
        window.open(link, '_blank');
      }, (index + 1) * 1500); // 1.5 second delay between each
    });
  };

  const previewUsers = selectedUsers.slice(0, 5);
  const hasMore = selectedUsers.length > 5;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: EMZI_RADIUS.lg,
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: EMZI_SHADOWS.xl
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${EMZI_COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderTopLeftRadius: EMZI_RADIUS.lg,
          borderTopRightRadius: EMZI_RADIUS.lg
        }}>
          <div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'white',
              margin: 0,
              marginBottom: '4px'
            }}>
              {t('admin.users.bulk.notifyModal.title')}
            </h2>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0
            }}>
              {t('admin.users.bulk.notifyModal.subtitle').replace('{count}', selectedUsers.length)}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: EMZI_RADIUS.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={24} color="white" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {/* Template Selector */}
          {loadingTemplates ? (
            <div style={{ padding: '20px', textAlign: 'center', color: EMZI_COLORS.textSecondary }}>
              Loading templates...
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  Select Message Template
                </label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    background: 'white'
                  }}
                >
                  {templates.map(template => (
                    <option key={template.template_id} value={template.template_id}>
                      {template.name} {template.is_default ? '(Default)' : ''} - {template.category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message Template Editor */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  marginBottom: '8px'
                }}>
                  {t('admin.users.bulk.notifyModal.message')}
                </label>
                <div style={{
                  fontSize: '12px',
                  color: EMZI_COLORS.textSecondary,
                  marginBottom: '8px'
                }}>
                  Available variables: <code style={{
                    padding: '2px 6px',
                    background: EMZI_COLORS.surface,
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '11px'
                  }}>{'{'}{'}'}name, email, role, login_url, temp_password, link_url, date, time, category, custom_field</code>
                </div>
                <textarea
                  value={messageTemplate}
                  onChange={(e) => setMessageTemplate(e.target.value)}
                  rows={8}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${EMZI_COLORS.border}`,
                    borderRadius: EMZI_RADIUS.md,
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = EMZI_COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = EMZI_COLORS.border}
                />
              </div>
            </>
          )}

          {/* Preview Section */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: EMZI_COLORS.textPrimary,
              marginBottom: '12px'
            }}>
              Message Preview ({previewUsers.length} of {selectedUsers.length})
            </h3>
            <div style={{
              maxHeight: '300px',
              overflow: 'auto',
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md
            }}>
              {previewUsers.map((user, idx) => (
                <div
                  key={`item-${idx}`}
                  style={{
                    padding: '16px',
                    borderBottom: idx < previewUsers.length - 1 ? `1px solid ${EMZI_COLORS.border}` : 'none',
                    background: idx % 2 === 0 ? 'white' : EMZI_COLORS.surface
                  }}
                >
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: EMZI_COLORS.textPrimary,
                    marginBottom: '4px'
                  }}>
                    {user.full_name || user.email}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: EMZI_COLORS.textSecondary,
                    marginBottom: '8px'
                  }}>
                    {user.phone_e164 || 'No phone number'}
                  </div>
                  <pre style={{
                    fontSize: '12px',
                    color: EMZI_COLORS.textSecondary,
                    background: 'white',
                    padding: '12px',
                    borderRadius: EMZI_RADIUS.sm,
                    border: `1px solid ${EMZI_COLORS.border}`,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'sans-serif',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    {generateMessage(user)}
                  </pre>
                </div>
              ))}
              {hasMore && (
                <div style={{
                  padding: '12px',
                  textAlign: 'center',
                  fontSize: '13px',
                  color: EMZI_COLORS.textSecondary,
                  fontStyle: 'italic'
                }}>
                  + {selectedUsers.length - 5} more users...
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            padding: '16px',
            background: EMZI_COLORS.surface,
            borderRadius: EMZI_RADIUS.md,
            marginBottom: '16px'
          }}>
            <div style={{
              fontSize: '12px',
              color: EMZI_COLORS.textSecondary,
              marginBottom: '12px'
            }}>
              ℹ️ {t('admin.users.bulk.notifyModal.sendingInstructions')}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px'
            }}>
              <button
                onClick={handleCopyAllLinks}
                style={{
                  padding: '12px',
                  background: 'white',
                  color: EMZI_COLORS.textPrimary,
                  border: `2px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minHeight: '48px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = EMZI_COLORS.primary;
                  e.currentTarget.style.color = EMZI_COLORS.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = EMZI_COLORS.border;
                  e.currentTarget.style.color = EMZI_COLORS.textPrimary;
                }}
              >
                <Copy size={18} />
                {t('admin.users.bulk.notifyModal.copyAllLinks')}
              </button>
              <button
                onClick={handleOpenSequentially}
                style={{
                  padding: '12px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minHeight: '48px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#059669';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#10b981';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                <ExternalLink size={18} />
                {t('admin.users.bulk.notifyModal.openSequentially')}
              </button>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '14px',
              background: 'white',
              color: EMZI_COLORS.textPrimary,
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkNotifyModal;
